import {
  CompanyFilters,
  GetCompaniesRequest,
  parseResolverResponse,
  BaseMutationOptions,
  ResolverMutationRequestMetadata,
  UpdateCompanyClientData,
  UpdateCompanyRequest,
  CreateCompanyClientData,
  CreateCompanyRequest,
  SoftDeleteCompanyClientData,
  SoftDeleteCompanyRequest
} from '@esg/syntiro-api-core';
import { CompanyExtended } from '../../lib/app/company';
import { DocumentNode } from 'graphql';
import { isValidBoolean, validateParams } from '../validators/base.validator';
import { isValidGroupId } from '../validators/group.validator';
import { apollo_client_v2 } from '../../App';
import { FetchResult } from '@apollo/client';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { FUNCTION_NO_DATA_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';
import { GET_COMPANIES_EXTENDED } from '../queries/company.queries';
import { Company } from '@esg/esg-global-types';
import { auth } from '../../lib/google/firebase';
import {
  CREATE_COMPANY,
  SOFT_DELETE_COMPANY,
  UPDATE_COMPANY
} from '../mutations/company.mutations';
import {
  isValidCompanyClientBigqueryDatasetId,
  isValidCompanyId,
  isValidCompanyLogoPath,
  isValidCompanyName,
  isValidCompanyStandards
} from '../validators/company.validator';

const SOURCE = 'company.service.ts';

/**
 * Query all companies for one or all groups.
 * @param {CompanyFilters} filters Filter parameters to use for companies query.
 * @param {DocumentNode | undefined} query Optionally pass in specific query to use for desired company fields.
 * @returns {Promise<Array<CompanyExtended>>}
 */
export const getCompanies = async (
  filters: CompanyFilters = {},
  query: DocumentNode = GET_COMPANIES_EXTENDED
): Promise<Array<CompanyExtended>> => {
  validateParams(
    { filters },
    {
      'filters.group_id': {
        validate: (group_id: unknown) => !group_id || isValidGroupId(group_id),
        message: 'Invalid group_id specified in filters.'
      }
    }
  );
  const input: GetCompaniesRequest['input'] = {
    metadata: {},
    filter: {
      deleted: false,
      ...filters
    }
  };
  return apollo_client_v2
    .query({
      variables: { input },
      query: query,
      fetchPolicy: 'no-cache'
    })
    .then((response: FetchResult) => {
      if (!response.data?.getCompanies) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('getCompanies'));
      }
      const api_result: Array<CompanyExtended> | null | undefined = parseResolverResponse<
        Array<CompanyExtended>
      >(response.data.getCompanies);
      return api_result ? api_result : [];
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'getCompanies'),
        { filters, query },
        tracking_id
      );
    });
};

/**
 * Create a company in database from required request data.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request containing group and company data.
 * @param {CreateCompanyClientData} data Required data to populate company fields.
 * @param {BaseMutationOptions | undefined} options Standard mutator options to pass to the API.
 * @returns {Promise<Company | null | undefined>}
 */
export const createCompany = async (
  metadata: ResolverMutationRequestMetadata,
  data: CreateCompanyClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<Company | null | undefined> => {
  validateParams(
    { metadata, data },
    {
      'metadata.group_id': {
        validate: isValidGroupId,
        message: 'Invalid group_id specified.'
      },
      'data.name': {
        validate: isValidCompanyName,
        message: 'Invalid company name specified.'
      },
      'data.standards': {
        validate: isValidCompanyStandards,
        message: 'Invalid company standards specified.'
      },
      'data.configuration.logo': {
        validate: isValidCompanyLogoPath,
        message: 'Invalid company logo path specified.'
      },
      'data.client_bigquery_dataset': {
        validate: isValidCompanyClientBigqueryDatasetId,
        message: 'Invalid client bigquery dataset id specified.'
      },
      'data.client_bigquery_dataset_sync': {
        validate: isValidBoolean,
        message: 'Invalid client bigquery dataset sync flag specified.'
      }
    }
  );

  const input: CreateCompanyRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      company: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: CREATE_COMPANY,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.createCompany) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('createCompany'));
      }
      return parseResolverResponse<Company>(response.data.createCompany);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createCompany'),
        { metadata, data },
        tracking_id
      );
    });
};

/**
 * Update a company in database from required request data.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request containing group and company data.
 * @param {UpdateCompanyClientData} data Required data to populate company fields.
 * @param {BaseMutationOptions | undefined} options Standard mutator options to pass to the API.
 * @returns {Promise<Company | null | undefined>}
 */
export const updateCompany = async (
  metadata: ResolverMutationRequestMetadata,
  data: UpdateCompanyClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<Company | null | undefined> => {
  validateParams(
    { metadata, data },
    {
      'metadata.group_id': {
        validate: isValidGroupId,
        message: 'Invalid group_id specified.'
      },
      'data.id': {
        validate: isValidCompanyId,
        message: 'Invalid company id specified.'
      },
      'data.name': {
        validate: isValidCompanyName,
        message: 'Invalid company name specified.',
        is_optional: true
      },
      'data.configuration.logo': {
        validate: isValidCompanyLogoPath,
        message: 'Invalid company logo path specified.',
        is_optional: true
      },
      'data.client_bigquery_dataset': {
        validate: isValidCompanyClientBigqueryDatasetId,
        message: 'Invalid client bigquery dataset id specified.',
        is_optional: true
      },
      'data.client_bigquery_dataset_sync': {
        validate: isValidBoolean,
        message: 'Invalid client bigquery dataset sync flag specified.',
        is_optional: true
      }
    }
  );

  const input: UpdateCompanyRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      company: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: UPDATE_COMPANY,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.updateCompany) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('updateCompany'));
      }
      return parseResolverResponse<Company>(response.data.updateCompany);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateCompany'),
        { metadata, data },
        tracking_id
      );
    });
};
/**
 * Soft delete a company in database from required request data.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request containing group and company data.
 * @param {SoftDeleteCompanyClientData} data Required data to delete a given company record.
 * @param {BaseMutationOptions | undefined} options Standard mutator options to pass to the API.
 * @returns {Promise<Group | null | undefined>}
 */
export const softDeleteCompany = async (
  metadata: ResolverMutationRequestMetadata,
  data: SoftDeleteCompanyClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<Company | null | undefined> => {
  validateParams(
    { metadata, data },
    {
      'metadata.group_id': {
        validate: isValidGroupId,
        message: 'Invalid group id specified.'
      },
      'data.id': {
        validate: isValidCompanyId,
        message: 'Invalid company id specified.'
      }
    }
  );

  const input: SoftDeleteCompanyRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      company: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: SOFT_DELETE_COMPANY,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.softDeleteCompany) {
        throw new Error('No data returned');
      }
      return parseResolverResponse<Company>(response.data.softDeleteCompany);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'softDeleteCompany'),
        { metadata, data },
        tracking_id
      );
    });
};
